'use client';

import { ArrowRight, X } from 'lucide-react';

import { cn } from '@kit/ui/utils';

import {
  INCREDIBLES_BAR_COOKIE,
  INCREDIBLES_BAR_COOKIE_VALUE,
  INCREDIBLES_BAR_HEIGHT,
  type IncrediblesBarVariant,
} from '~/lib/incredibles-bar';

import { IncrediblesLogo } from './incredibles-logo';

const ARTICLE_URL =
  'https://incredibles.pl/aktualnosci/incredibles/lekolizja-kolejnym-finalista-10-edycji-incredibles';

const VARIANTS: Record<
  IncrediblesBarVariant,
  {
    bar: string;
    marker: string;
    /** Background of the pill that lights up behind the text on hover */
    hover: string;
    name: string;
    arrow: string;
    dismiss: string;
    /** The band of light sweeping across the marker (keyframes in globals.css) */
    shine: string;
  }
> = {
  citron: {
    bar: 'bg-[#FEF08A] text-zinc-900 dark:bg-[#2e2b1a] dark:text-zinc-100',
    // Mirror image of the black bar: dark marker, letters in the bar color
    marker: 'bg-zinc-900 text-[#FEF08A] dark:bg-[#EBC760] dark:text-zinc-900',
    hover: 'group-hover:bg-zinc-900/10 dark:group-hover:bg-white/10',
    name: 'text-zinc-600 group-hover:text-zinc-900 dark:text-zinc-400 dark:group-hover:text-zinc-100',
    arrow: '',
    dismiss:
      'text-zinc-500 hover:bg-zinc-900/10 hover:text-zinc-900 dark:text-zinc-400 dark:hover:bg-white/10 dark:hover:text-white',
    // Repeats every 7 s
    shine: 'inc-shine-loop 7s ease-in-out 1200ms infinite both',
  },
  black: {
    bar: 'bg-zinc-800 text-zinc-100 dark:bg-zinc-900',
    marker: 'bg-[#EBC760] text-zinc-900',
    hover: 'group-hover:bg-white/25',
    name: 'text-zinc-300 group-hover:text-white',
    arrow: 'text-[#EBC760]',
    dismiss: 'text-zinc-500 hover:bg-zinc-700 hover:text-white',
    // Once, shortly after load
    shine: 'inc-shine 900ms ease-in-out 1200ms both',
  },
};

function setDismissedCookie() {
  const secure = window.location.protocol === 'https:';
  document.cookie = `${INCREDIBLES_BAR_COOKIE}=${INCREDIBLES_BAR_COOKIE_VALUE}; path=/; max-age=31536000; SameSite=Lax${secure ? '; Secure' : ''}`;
}

/**
 * "Finalista X edycji InCredibles" announcement bar. The whole bar is one link
 * to the article; hovering anywhere lights up a pill behind the text. The close
 * button sits on top of the link and remembers the dismissal in a cookie.
 */
export function IncrediblesBar({
  variant,
  onDismiss,
}: {
  variant: IncrediblesBarVariant;
  onDismiss: () => void;
}) {
  const tone = VARIANTS[variant];

  const handleDismiss = () => {
    setDismissedCookie();
    onDismiss();
  };

  return (
    <div
      className={cn('relative', tone.bar)}
      style={{ height: INCREDIBLES_BAR_HEIGHT }}
    >
      <a
        href={ARTICLE_URL}
        target="_blank"
        rel="noreferrer noopener"
        className="group absolute inset-0 flex items-center justify-center px-5 focus-visible:outline-2 focus-visible:-outline-offset-2 sm:px-9"
      >
        <span
          className={cn(
            'inline-flex min-w-0 items-center gap-1.5 rounded-sm px-2 py-[3px] text-[10px] leading-4 whitespace-nowrap transition-colors sm:gap-2.5',
            tone.hover,
          )}
        >
          <span
            className={cn(
              'font-heading relative overflow-hidden px-1 py-[2px] text-[9px] leading-none font-bold tracking-[0.12em] uppercase',
              tone.marker,
            )}
          >
            Finalista X edycji
            <span className="hidden sm:inline"> (2026)</span>
            <span
              aria-hidden
              className="inc-shine pointer-events-none absolute inset-y-0 left-0 w-1/2 bg-gradient-to-r from-transparent via-white/80 to-transparent"
              style={{
                transform: 'translateX(-150%) skewX(-20deg)',
                animation: tone.shine,
              }}
            />
          </span>
          <IncrediblesLogo className="h-[14.6px] w-auto shrink-0 translate-y-[1.6px]" />
          <span
            className={cn(
              'min-w-0 truncate text-[11px] transition-colors',
              tone.name,
            )}
          >
            <span className="sm:hidden">S. Kulczyka</span>
            <span className="hidden sm:inline">Sebastiana Kulczyka</span>
          </span>
          <ArrowRight
            className={cn(
              'h-[11px] w-[11px] shrink-0 transition-transform group-hover:translate-x-0.5',
              tone.arrow,
            )}
          />
        </span>
      </a>
      <button
        type="button"
        aria-label="Zamknij"
        onClick={handleDismiss}
        className={cn(
          'absolute top-1/2 right-0.5 z-10 -translate-y-1/2 cursor-pointer rounded p-0.5 transition-colors sm:right-2',
          tone.dismiss,
        )}
      >
        <X className="h-3 w-3" />
      </button>
    </div>
  );
}
